/**
 * Voice Input Button Component
 * Reusable button for speech-to-text input across the application
 * 
 * Usage:
 * <VoiceInputButton 
 *   onTranscript={(text) => setInput(text)}
 *   language="te-IN"
 *   existingText={input}
 * />
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Snackbar,
  Alert,
  Badge
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Stop as StopIcon
} from '@mui/icons-material';
import {
  createSpeechRecognition,
  isSpeechRecognitionSupported,
  detectLanguageForRecognition
} from '../services/speechRecognitionService';

function VoiceInputButton({ 
  onTranscript, 
  language = 'en-IN',
  existingText = '',
  disabled = false,
  size = 'medium',
  color = 'primary'
}) {
  const [isListening, setIsListening] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState(null);
  const [isSupported] = useState(isSpeechRecognitionSupported());
  const recognitionRef = useRef(null);
  const previousLanguageRef = useRef(language); // v11.12.34: Track previous language
  
  // v11.12.32: Use language prop directly, no auto-detection (for explicit language control)
  // Only auto-detect if existingText has content AND language is default
  // v11.12.39: Map Sanskrit to Hindi for voice input (Web Speech API doesn't support Sanskrit transcription)
  const detectedLanguage = (existingText && language === 'en-IN') 
    ? detectLanguageForRecognition(existingText) 
    : (language === 'sa-IN' ? 'hi-IN' : language);

  // v11.12.32: Define stopListening with useCallback
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        // Ignore errors if already stopped
        console.log('🎤 Recognition already stopped or aborted');
      }
    }
    setIsListening(false);
    setInterimText('');
  }, []);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (err) {
          // Ignore errors on cleanup
        }
      }
    };
  }, []);
  
  // v11.12.34: Only stop listening if language actually changed (not just re-render)
  useEffect(() => {
    if (language !== previousLanguageRef.current && isListening) {
      console.log(`🎤 Language changed from ${previousLanguageRef.current} to ${language}, stopping recognition`);
      stopListening();
      previousLanguageRef.current = language;
    } else {
      previousLanguageRef.current = language;
    }
  }, [language, isListening, stopListening]);

  const startListening = () => {
    if (!isSupported) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    setError(null);
    setInterimText('');

    // v11.12.32: Log the language being used
    console.log(`🎤 Starting voice recognition in: ${getLanguageName(detectedLanguage)} (${detectedLanguage})`);

    // Create recognition instance
    recognitionRef.current = createSpeechRecognition({
      language: detectedLanguage,
      continuous: false, // Stop after one phrase
      interimResults: true,
      onResult: ({ finalTranscript, interimTranscript, isFinal }) => {
        if (isFinal && finalTranscript) {
          // Append to existing text if any
          const newText = existingText ? `${existingText} ${finalTranscript}` : finalTranscript;
          onTranscript(newText);
          setInterimText('');
          setIsListening(false);
        } else {
          setInterimText(interimTranscript);
        }
      },
      onError: ({ message }) => {
        // v11.12.34: Don't show error for abort (happens when stopping or switching languages)
        if (message && !message.toLowerCase().includes('abort')) {
          setError(message);
        } else {
          console.log('🎤 Recognition aborted (expected behavior)');
        }
        setIsListening(false);
        setInterimText('');
      },
      onStart: () => {
        setIsListening(true);
        console.log('🎤 Voice input started');
      },
      onEnd: () => {
        setIsListening(false);
        setInterimText('');
        console.log('🛑 Voice input ended');
      }
    });

    // v11.12.34: Add try-catch for start to handle any errors gracefully
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
      } catch (err) {
        console.error('🎤 Failed to start recognition:', err);
        setError('Failed to start voice input. Please try again.');
        setIsListening(false);
      }
    }
  };

  const handleClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  if (!isSupported) {
    return (
      <Tooltip title="Voice input not supported in this browser">
        <span>
          <IconButton disabled size={size}>
            <MicOffIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const tooltipTitle = isListening 
    ? `Listening... (${getLanguageName(detectedLanguage)})` 
    : `Click to speak (${getLanguageName(detectedLanguage)})`;

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={disabled}
            size={size}
            color={isListening ? 'error' : color}
            sx={{
              animation: isListening ? 'pulse 1.5s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.1)' }
              }
            }}
          >
            {isListening ? (
              <Badge
                badgeContent={
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: 'error.main',
                      animation: 'blink 1s ease-in-out infinite',
                      '@keyframes blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.3 }
                      }
                    }}
                  />
                }
                color="error"
                overlap="circular"
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MicIcon />
              </Badge>
            ) : (
              <MicIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>

      {/* Interim text display */}
      {interimText && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            bgcolor: 'info.light',
            color: 'info.dark',
            px: 2,
            py: 1,
            borderRadius: 1,
            mb: 1,
            fontSize: '0.875rem',
            fontStyle: 'italic',
            boxShadow: 1,
            zIndex: 10
          }}
        >
          Listening: "{interimText}..."
        </Box>
      )}

      {/* Error snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

// Helper function to get readable language name
// v11.12.32: Added Sanskrit and updated language names
function getLanguageName(languageCode) {
  const languageNames = {
    'en-IN': 'English',
    'hi-IN': 'Hindi',
    'te-IN': 'Telugu',
    'ta-IN': 'Tamil',
    'kn-IN': 'Kannada',
    'ml-IN': 'Malayalam',
    'bn-IN': 'Bengali',
    'mr-IN': 'Marathi',
    'gu-IN': 'Gujarati',
    'pa-IN': 'Punjabi',
    'ur-IN': 'Urdu',
    'sa-IN': 'Sanskrit' // Note: sa-IN is mapped to hi-IN for voice input (v11.12.39)
  };
  return languageNames[languageCode] || 'English';
}

export default VoiceInputButton;

